import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, errMsg, imgSrc, money, RARITY } from "@/lib/api";
import { PageTitle } from "@/components/admin/AdminLayout";
import { ImageUpload } from "@/components/admin/ImageUpload";

const EMPTY = { name: "", description: "", category_id: "none", price_brl: "", price_eur: "", image: "", stock: "", rarity: "comum", featured: false, active: true, commands: "" };

const ProductForm = ({ edit, setEdit, cats, onSave }) => {
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });
  return (
    <form onSubmit={onSave} className="space-y-4" data-testid="admin-product-form">
      <ImageUpload value={edit.image} onChange={(p) => setEdit({ ...edit, image: p })} />
      <div className="space-y-2"><Label>Nome</Label><Input required value={edit.name} onChange={set("name")} data-testid="admin-product-name-input" /></div>
      <div className="space-y-2"><Label>Descrição</Label><Textarea value={edit.description} onChange={set("description")} data-testid="admin-product-description-input" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Preço Pix (R$)</Label><Input required type="number" step="0.01" min="0" value={edit.price_brl} onChange={set("price_brl")} data-testid="admin-product-price-brl-input" /></div>
        <div className="space-y-2"><Label>Preço MB Way (€)</Label><Input required type="number" step="0.01" min="0" value={edit.price_eur} onChange={set("price_eur")} data-testid="admin-product-price-eur-input" /></div>
        <div className="space-y-2"><Label>Categoria</Label>
          <Select value={edit.category_id} onValueChange={(v) => setEdit({ ...edit, category_id: v })}>
            <SelectTrigger data-testid="admin-product-category-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Sem categoria</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Raridade</Label>
          <Select value={edit.rarity} onValueChange={(v) => setEdit({ ...edit, rarity: v })}>
            <SelectTrigger data-testid="admin-product-rarity-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(RARITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2 col-span-2"><Label>Estoque (deixe vazio para ilimitado)</Label><Input type="number" min="0" value={edit.stock} onChange={set("stock")} data-testid="admin-product-stock-input" /></div>
        <div className="space-y-2 col-span-2">
          <Label>Comandos de entrega no Minecraft (um por linha)</Label>
          <Textarea rows={3} className="font-mono text-xs" placeholder={"give {player} diamond 64\nlp user {player} parent add vip"} value={edit.commands} onChange={set("commands")} data-testid="admin-product-commands-input" />
          <p className="text-xs text-slate-500">Use <span className="font-mono text-emerald-300">{"{player}"}</span> para o nick do comprador. Os comandos repetem para cada unidade comprada.</p>
        </div>
      </div>
      <div className="flex gap-6">
        <div className="flex items-center gap-2"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="admin-product-active-switch" /><Label>Visível</Label></div>
        <div className="flex items-center gap-2"><Switch checked={edit.featured} onCheckedChange={(v) => setEdit({ ...edit, featured: v })} data-testid="admin-product-featured-switch" /><Label>Destaque</Label></div>
      </div>
      <button className="w-full h-11 rounded-full bg-violet-600 hover:bg-violet-500 font-semibold" data-testid="admin-product-save-button">Salvar produto</button>
    </form>
  );
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const load = () => http.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); http.get("/admin/categories").then((r) => setCats(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    const { id, created_at, ...b } = edit;
    const body = { ...b, commands: (b.commands || "").split("\n").map((c) => c.trim()).filter(Boolean), category_id: b.category_id === "none" ? null : b.category_id, price_brl: Number(b.price_brl), price_eur: Number(b.price_eur), stock: b.stock === "" || b.stock === null ? null : Number(b.stock) };
    try {
      if (id) await http.put(`/admin/products/${id}`, body); else await http.post("/admin/products", body);
      toast.success("Produto salvo"); setEdit(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    await http.delete(`/admin/products/${p.id}`); toast.success("Produto excluído"); load();
  };

  return (
    <div>
      <PageTitle title="Produtos" sub={`${items.length} produtos`}>
        <button onClick={() => setEdit(EMPTY)} data-testid="admin-product-create-button" className="h-10 px-5 rounded-full bg-violet-600 hover:bg-violet-500 text-sm font-semibold inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Novo produto</button>
      </PageTitle>
      <div className="rounded-2xl bg-[#111827] border border-white/10 divide-y divide-white/5" data-testid="admin-product-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.id}`}>
            <img src={imgSrc(p.image)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate flex items-center gap-2">{p.name}{p.featured && <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />}</p>
              <p className="text-xs text-slate-500 truncate">{catMap[p.category_id]?.name || "Sem categoria"} · {RARITY[p.rarity]?.label} · {p.stock ?? "∞"} em estoque · {(p.commands || []).length} comando(s) {!p.active && "· Oculto"}</p>
            </div>
            <div className="hidden sm:block text-right font-mono text-sm"><p>{money(p.price_brl, "BRL")}</p><p className="text-slate-500">{money(p.price_eur, "EUR")}</p></div>
            <button onClick={() => setEdit({ ...p, category_id: p.category_id || "none", stock: p.stock ?? "", commands: (p.commands || []).join("\n") })} className="p-2 text-slate-400 hover:text-white" data-testid={`admin-product-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p)} className="p-2 text-slate-400 hover:text-rose-400" data-testid={`admin-product-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{edit?.id ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          {edit && <ProductForm edit={edit} setEdit={setEdit} cats={cats} onSave={save} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
